// 0 = Count/None (Stück/Anzahl)
// 1 = Length (Länge - m/cm)
// 2 = Area (Fläche - m²)
// 3 = Volume (Volumen - m³)
export type DamageOptionItem = {
	label: string;
	dimension: 0 | 1 | 2 | 3;
};

export type DamageGroup = {
	name: string;
	options: DamageOptionItem[];
};

export const STATUS_OPTIONS = ['Schaden', 'Schadensverdacht', 'Fehlerkennung', 'Instandgesetzt'];
export const ACTIVE_STATUS_OPTIONS = STATUS_OPTIONS.filter((status) => status !== 'Instandgesetzt');
export const OBJECT_CATEGORY_OPTIONS = [
	'Konstruktion',
	'Ausrüstung',
	'Dichtungen',
	'Festmachevorrichtungen',
	'Fugen',
	'Korrosionsschutz',
	'Soll- und Uferbauten',
];
export const OBJECT_SUBCATEGORY_OPTIONS = ['Konstruktion'];

export const MATERIAL_GROUPS: Record<string, string[]> = {
	Beton: [
		'Mörtel',
		'Mörtel/Beton mit Kunststoffzusatz (PCCI)',
		'Schleuderbeton',
		'Spannbeton',
		'Spritzbeton',
		'Spritzmörtel/Spritzbeton mit Kunststoffzusatz (SPCC)',
		'Stahlbeton',
		'Stampfbeton',
		'Unbewehrter Beton',
	],
	Bewehrung: [
		'Betonstahl Gruppe 1',
		'Betonstahl Gruppe 2',
		'Betonstahl Gruppe 3',
		'Profilstahl',
		'Spannstahl',
	],
	Mauerwerk: ['Betonstein', 'Kalksandstein', 'Klinker', 'Mauerziegel', 'Mörtel', 'Naturstein'],
	Steine: [
		'Geotextil',
		'Kies',
		'Mineral',
		'Naturstein',
		'Pflasterung',
		'Sand',
		'Steinmatten',
		'Steinschüttung',
		'Steinschüttung mit Teilverguss',
		'Steinschüttung mit Vollverguss',
		'Ton',
		'Unvergossene Steinschüttung',
		'Wasserbausteine',
	],
	Metall: [
		'Unlegierter Stahl (Baustahl)',
		'Legierter Stahl (Edelstahl)',
		'Stahlguss',
		'Gusseisen',
		'Aluminium',
		'Bronze',
		'Messing',
		'Titanzink',
		'Kupfer',
	],
	Kunststoffe: [
		'Acrylat',
		'Elastomer',
		'Epoxidharz',
		'Gewebeverstärktes Elastomer',
		'GFK (glasfaserverstärkte Kunststoffe)',
		'Polyamid',
		'Polyester',
		'Polyethylen',
		'Polypropylen',
		'Polyurethan',
		'PVC',
	],
	Sonstiges: ['Bitumen', 'Fliesen', 'Glas', 'Gummi', 'Holz', 'Mutterboden', 'Asphalt'],
};

// --- DAMAGE GROUPS WITH DIMENSIONS ---

export const DAMAGE_GROUP_ALLGEMEIN: DamageGroup = {
	name: 'Allgemein (Schadensbewertung / Zustandsbeschreibung)',
	options: [
		{ label: 'Abgerissen', dimension: 0 },
		{ label: 'Ausgeschlagen', dimension: 0 },
		{ label: 'Behindert', dimension: 0 },
		{ label: 'Deformiert', dimension: 0 },
		{ label: 'Falsch', dimension: 0 },
		{ label: 'Fehlstelle', dimension: 0 },
		{ label: 'Fehlt', dimension: 0 },
		{ label: 'Festsitzend', dimension: 0 },
		{ label: 'Fremdkörpereinlage', dimension: 0 },
		{ label: 'Gerissen', dimension: 1 },
		{ label: 'Hindernisse', dimension: 0 },
		{ label: 'Locker', dimension: 0 },
		{ label: 'Nicht einsehbar', dimension: 0 },
		{ label: 'Nicht fachgerecht', dimension: 0 },
		{ label: 'Nicht funktionsfähig', dimension: 0 },
		{ label: 'Nicht gemäß DIN', dimension: 0 },
		{ label: 'Nicht gemäß Statik', dimension: 0 },
		{ label: 'Nicht gemäß Unterlagen', dimension: 0 },
		{ label: 'Nicht gemäß UVV', dimension: 0 },
		{ label: 'Nicht trockengelegt', dimension: 0 },
		{ label: 'Nicht verfugt', dimension: 1 },
		{ label: 'Nicht verfüllt', dimension: 1 },
		{ label: 'Nicht vergossen', dimension: 1 },
		{ label: 'Nicht verpresst', dimension: 1 },
		{ label: 'Nicht verschiebbar', dimension: 0 },
		{ label: 'Nicht versetzt', dimension: 0 },
		{ label: 'Nicht wirksam', dimension: 0 },
		{ label: 'Nicht zugänglich', dimension: 0 },
		{ label: 'Nicht kraftschlüssig', dimension: 0 },
		{ label: 'Offen', dimension: 0 },
		{ label: 'Schadhaft', dimension: 0 },
		{ label: 'Schwergängig', dimension: 0 },
		{ label: 'Unleserlich', dimension: 0 },
		{ label: 'Unvollständig', dimension: 0 },
		{ label: 'Verschleiß', dimension: 0 },
		{ label: 'Undicht', dimension: 0 },
		{ label: 'Ungleichmäßig', dimension: 0 },
	],
};

export const DAMAGE_GROUP_BETON: DamageGroup = {
	name: 'Beton',
	options: [
		{ label: 'Feinkorn kuppenartig freiliegend', dimension: 2 },
		{ label: 'Feuchtfläche', dimension: 2 },
		{ label: 'Grobkorn kuppenartig freiliegend', dimension: 2 },
		{ label: 'Hohlstelle', dimension: 2 },
		{ label: 'Kantenabbruch', dimension: 2 },
		{ label: 'Kiesnest', dimension: 2 },
		{ label: 'Lunker', dimension: 2 },
		{ label: 'Nassfläche', dimension: 2 },
		{ label: 'Oberfläche absandend', dimension: 2 },
		{ label: 'Oberfläche hauwerksporig', dimension: 2 },
		{ label: 'Oberfläche offenporig', dimension: 2 },
		{ label: 'Punktueller Wasseraustritt', dimension: 2 },
		{ label: 'Rostfahne', dimension: 2 },
		{ label: 'Schalenablösung', dimension: 2 },
		{ label: 'Wasserführend mit Aussinterung', dimension: 2 },
	],
};

export const DAMAGE_GROUP_BEWEHRUNG: DamageGroup = {
	name: 'Bewehrung',
	options: [
		{ label: 'Lochkorrosion', dimension: 0 },
		{ label: 'Angreifen', dimension: 0 },
		{ label: 'Deformiert', dimension: 0 },
		{ label: 'Duktil gerissen', dimension: 1 },
		{ label: 'Freiliegend', dimension: 2 },
		{ label: 'Gleichmäßig abtragende Korrosion', dimension: 2 },
		{ label: 'Narbenkorrosion', dimension: 1 },
		{ label: 'Spröde gerissen', dimension: 1 },
	],
};

export const DAMAGE_GROUP_FORM: DamageGroup = {
	name: 'Form- und Lageänderung',
	options: [
		{ label: 'Abgesackt', dimension: 0 },
		{ label: 'Anliegend', dimension: 0 },
		{ label: 'Aufgewölbt', dimension: 2 },
		{ label: 'Aus Halterung gerutscht', dimension: 0 },
		{ label: 'Ausgebaut', dimension: 0 },
		{ label: 'Ausgebrochen', dimension: 2 },
		{ label: 'Außer Lot', dimension: 0 },
		{ label: 'Deformiert', dimension: 0 },
		{ label: 'Durchgebrochen', dimension: 0 },
		{ label: 'Erosion', dimension: 2 },
		{ label: 'Gebrochen', dimension: 0 },
		{ label: 'Gekippt', dimension: 0 },
		{ label: 'Gerissen', dimension: 1 },
		{ label: 'Gestaucht', dimension: 0 },
		{ label: 'Gleichmäßige Setzung', dimension: 0 },
		{ label: 'Hervorstehend', dimension: 0 },
		{ label: 'Lage falsch', dimension: 0 },
		{ label: 'Nicht parallel', dimension: 0 },
		{ label: 'Rutschung', dimension: 0 },
		{ label: 'Schrägstellung', dimension: 0 },
		{ label: 'Uneben', dimension: 0 },
		{ label: 'Ungleichmäßige Setzung', dimension: 0 },
		{ label: 'Ungleichmäßige Weite', dimension: 0 },
		{ label: 'Verbaut', dimension: 0 },
		{ label: 'Verbogen', dimension: 0 },
		{ label: 'Verdreht', dimension: 0 },
		{ label: 'Verdrillt', dimension: 0 },
		{ label: 'Verkanten', dimension: 0 },
		{ label: 'Versatz', dimension: 0 },
		{ label: 'Verschoben', dimension: 0 },
	],
};

export const DAMAGE_GROUP_GROESSE: DamageGroup = {
	name: 'Größenangaben',
	options: [
		{ label: 'Abstand zu groß', dimension: 0 },
		{ label: 'Abstand zu klein', dimension: 0 },
		{ label: 'Breite zu gering', dimension: 0 },
		{ label: 'Dehnweg zu gering', dimension: 0 },
		{ label: 'Gefälle zu klein', dimension: 0 },
		{ label: 'Höhe zu gering', dimension: 0 },
		{ label: 'Nicht ausreichend', dimension: 0 },
		{ label: 'Spiel zu groß', dimension: 0 },
		{ label: 'Spiel zu klein', dimension: 0 },
		{ label: 'Unterschiedlich', dimension: 0 },
		{ label: 'Zu breit', dimension: 0 },
		{ label: 'Zu gering', dimension: 0 },
		{ label: 'Zu groß', dimension: 0 },
		{ label: 'Zu hoch', dimension: 0 },
		{ label: 'Zu steil', dimension: 0 },
		{ label: 'Zu tief', dimension: 0 },
		{ label: 'Zu kurz', dimension: 0 },
	],
};

export const DAMAGE_GROUP_RISSE: DamageGroup = {
	name: 'Risse',
	options: [
		{ label: 'Gerissen', dimension: 1 },
		{ label: 'Horizontale und vertikale Risse (trocken)', dimension: 1 },
		{ label: 'Horizontale und vertikale Risse (feucht)', dimension: 1 },
		{ label: 'Horizontalriss (feucht)', dimension: 1 },
		{ label: 'Horizontalriss (trocken)', dimension: 1 },
		{ label: 'Horizontalriss (wasserführend)', dimension: 1 },
		{ label: 'Horizontalriss (wasserführend mit Bodenaustrag)', dimension: 1 },
		{ label: 'Längsriss (feucht)', dimension: 1 },
		{ label: 'Längsriss (trocken)', dimension: 1 },
		{ label: 'Längsriss (wasserführend)', dimension: 1 },
		{ label: 'Längsriss (wasserführend mit Bodenaustrag)', dimension: 1 },
		{ label: 'Netzrisse (feucht)', dimension: 1 },
		{ label: 'Netzrisse (trocken)', dimension: 1 },
		{ label: 'Querriss (feucht)', dimension: 1 },
		{ label: 'Querriss (trocken)', dimension: 1 },
		{ label: 'Querriss (wasserführend)', dimension: 1 },
		{ label: 'Querriss (wasserführend mit Bodenaustrag)', dimension: 1 },
		{ label: 'Riss mit Ausmiterung', dimension: 1 },
		{ label: 'Schrägriss (feucht)', dimension: 1 },
		{ label: 'Schrägriss (trocken)', dimension: 1 },
		{ label: 'Schrägriss (wasserführend)', dimension: 1 },
		{ label: 'Schrägriss (wasserführend mit Bodenaustrag)', dimension: 1 },
		{ label: 'Vertikalriss (feucht)', dimension: 1 },
		{ label: 'Vertikalriss (trocken)', dimension: 1 },
		{ label: 'Vertikalriss (wasserführend)', dimension: 1 },
		{ label: 'Vertikalriss (wasserführend mit Bodenaustrag)', dimension: 1 },
	],
};

export const DAMAGE_GROUP_SAUBERKEIT: DamageGroup = {
	name: 'Sauberkeit',
	options: [
		{ label: 'Ablagerung', dimension: 2 },
		{ label: 'Bemoost', dimension: 2 },
		{ label: 'Bewachsen', dimension: 2 },
		{ label: 'Nicht gereinigt', dimension: 2 },
		{ label: 'Verschmutzt', dimension: 0 },
		{ label: 'Versandet', dimension: 0 },
		{ label: 'Verstopft', dimension: 0 },
	],
};

export const DAMAGE_GROUP_WASSER: DamageGroup = {
	name: 'Wasserschäden',
	options: [
		{ label: 'Ablauf mangelhaft', dimension: 0 },
		{ label: 'Feucht', dimension: 2 },
		{ label: 'Geröllablagerung', dimension: 2 },
		{ label: 'Schlammablagerung', dimension: 2 },
		{ label: 'Treibgutansammlung', dimension: 2 },
		{ label: 'Unterläufig', dimension: 2 },
		{ label: 'Unterspült', dimension: 2 },
		{ label: 'Kolk', dimension: 3 }, // Volume
		{ label: 'Wasserauspülung', dimension: 2 },
		{ label: 'Wasserführend', dimension: 0 },
		{ label: 'Wasserstau', dimension: 0 },
	],
};
export const DAMAGE_GROUP_MAUERWERK: DamageGroup = {
	name: 'Mauerwerk und Mörtel',
	options: [
		{ label: 'Abgearbeitet', dimension: 2 },
		{ label: 'Ablösung der Verblendschale', dimension: 2 },
		{ label: 'Abplatzung', dimension: 2 },
		{ label: 'Abwitterung', dimension: 2 },
		{ label: 'Ausbruch', dimension: 2 },
		{ label: 'Feuchtfläche', dimension: 2 },
		{ label: 'Fugenmörtel schadhaft', dimension: 1 },
		{ label: 'Gerissen', dimension: 1 },
		{ label: 'Hohlstelle', dimension: 2 },
		{ label: 'Kantenabbruch', dimension: 1 },
		{ label: 'Mörtelfuge mit Ausblühung', dimension: 1 },
		{ label: 'Mörtelfuge mit Ausmiterungen', dimension: 1 },
		{ label: 'Mörtelfuge mit Wasseraustritt', dimension: 1 },
		{ label: 'Nassfläche', dimension: 2 },
		{ label: 'Punktueller Wasseraustritt', dimension: 0 },
		{ label: 'Stein mit Ausblühungen', dimension: 2 },
		{ label: 'Stein schadhaft örtlich', dimension: 2 },
		{ label: 'Steine schadhaft flächig', dimension: 2 },
		{ label: 'Treppenförmiger Rissverlauf', dimension: 1 },
	],
};

export const DAMAGE_GROUP_KUNSTSTOFF: DamageGroup = {
	name: 'Kunststoff und Elastomer',
	options: [
		{ label: 'Abgerissen', dimension: 0 },
		{ label: 'Ablösung an Fügstelle', dimension: 0 },
		{ label: 'Abrasion', dimension: 0 },
		{ label: 'Beschädigt', dimension: 0 },
		{ label: 'Deformiert', dimension: 0 },
		{ label: 'Fehlt', dimension: 0 },
		{ label: 'Feucht', dimension: 0 },
		{ label: 'Gedrückt', dimension: 0 },
		{ label: 'Gerissen', dimension: 0 },
		{ label: 'Herausgedrückt', dimension: 0 },
		{ label: 'Herausgequellt', dimension: 0 },
		{ label: 'Kanten ausgebrochen', dimension: 1 },
		{ label: 'Locker', dimension: 0 },
		{ label: 'Nicht verschlossen', dimension: 0 },
		{ label: 'Riss an Fügstelle', dimension: 1 },
		{ label: 'Riss in der Membran', dimension: 1 },
		{ label: 'Undicht', dimension: 0 },
		{ label: 'Versprödet', dimension: 0 },
		{ label: 'Wasserführend', dimension: 0 },
		{ label: 'Wasserführend mit Bodenaustrag', dimension: 0 },
	],
};

export const DAMAGE_GROUP_VERBINDUNG: DamageGroup = {
	name: 'Verbindungsmittel',
	options: [
		{ label: 'Angerissen/Gerissen', dimension: 0 },
		{ label: 'Fehlt und/oder lose', dimension: 0 },
		{ label: 'Lose/Ausgeschlagen', dimension: 0 },
		{ label: 'Unter- oder ausgerostet', dimension: 0 },
		{ label: 'Unterbrochen', dimension: 0 },
		{ label: 'Abgerostet', dimension: 0 },
	],
};

// Assuming dimensions for Holz were not explicitly provided in numbers, assuming standard structural logic or default 0/2 based on text.
export const DAMAGE_GROUP_HOLZ: DamageGroup = {
	name: 'Holz',
	options: [
		{ label: 'Abgearbeitetes', dimension: 2 },
		{ label: 'Feuchtfläche', dimension: 2 },
		{ label: 'Gebrochen', dimension: 0 },
		{ label: 'Gerissen', dimension: 1 },
		{ label: 'Gespalten', dimension: 0 },
		{ label: 'Insektenbefall', dimension: 2 },
		{ label: 'Morsch', dimension: 2 },
		{ label: 'Nassfäule', dimension: 2 },
		{ label: 'Pilzbefall', dimension: 2 },
		{ label: 'Punktueller Wasseraustritt', dimension: 0 },
		{ label: 'Verfault', dimension: 2 },
		{ label: 'Verwittert', dimension: 2 },
	],
};

export const MATERIAL_DAMAGE_MAPPING: Record<string, DamageGroup[]> = {
	Beton: [
		DAMAGE_GROUP_ALLGEMEIN,
		DAMAGE_GROUP_BETON,
		DAMAGE_GROUP_BEWEHRUNG,
		DAMAGE_GROUP_FORM,
		DAMAGE_GROUP_GROESSE,
		DAMAGE_GROUP_RISSE,
		DAMAGE_GROUP_SAUBERKEIT,
		DAMAGE_GROUP_WASSER,
	],
	Bewehrung: [DAMAGE_GROUP_BETON, DAMAGE_GROUP_BEWEHRUNG],
	Mauerwerk: [
		DAMAGE_GROUP_ALLGEMEIN,
		DAMAGE_GROUP_FORM,
		DAMAGE_GROUP_GROESSE,
		DAMAGE_GROUP_MAUERWERK,
		DAMAGE_GROUP_RISSE,
		DAMAGE_GROUP_SAUBERKEIT,
		DAMAGE_GROUP_WASSER,
	],
	Steine: [
		DAMAGE_GROUP_ALLGEMEIN,
		DAMAGE_GROUP_BETON,
		DAMAGE_GROUP_BEWEHRUNG,
		DAMAGE_GROUP_FORM,
		DAMAGE_GROUP_GROESSE,
		DAMAGE_GROUP_MAUERWERK,
		DAMAGE_GROUP_RISSE,
		DAMAGE_GROUP_SAUBERKEIT,
		DAMAGE_GROUP_WASSER,
	],
	Metall: [
		DAMAGE_GROUP_ALLGEMEIN,
		DAMAGE_GROUP_FORM,
		DAMAGE_GROUP_GROESSE,
		DAMAGE_GROUP_SAUBERKEIT,
	],
	Kunststoffe: [
		DAMAGE_GROUP_ALLGEMEIN,
		DAMAGE_GROUP_FORM,
		DAMAGE_GROUP_KUNSTSTOFF,
		DAMAGE_GROUP_GROESSE,
		DAMAGE_GROUP_SAUBERKEIT,
		DAMAGE_GROUP_VERBINDUNG,
	],
	Sonstiges: [
		DAMAGE_GROUP_ALLGEMEIN,
		DAMAGE_GROUP_BETON,
		DAMAGE_GROUP_BEWEHRUNG,
		DAMAGE_GROUP_FORM,
		DAMAGE_GROUP_GROESSE,
		DAMAGE_GROUP_HOLZ,
		DAMAGE_GROUP_SAUBERKEIT,
		DAMAGE_GROUP_VERBINDUNG,
	],
};

export const DAMAGE_CLASSES = [
	{
		value: '1 - ohne Gefährdung',
		color: 'bg-blue-600',
	},
	{
		value: '2 - langfristige Gefährdung',
		color: 'bg-yellow-600',
	},
	{
		value: '3 - mittelfristige Gefährdung',
		color: 'bg-orange-600',
	},
	{
		value: '4 - akute Gefährdung',
		color: 'bg-red-600',
	},
];

export const REMARK_OPTIONS = [
	'Fremdinstitut einschalten',
	'Gefahr für Prüfpersonal',
	'Gutachten erforderlich',
	'Siehe Anlage',
	'Siehe letzte Prüfung',
	'Siehe letzte Sonderprüfung',
	'Siehe letzte Überwachung',
];
export const EXTENT_OPTIONS = [
	'All',
	'allgemein',
	'an allen Bauteilen',
	'an einigen Bauteilen',
	'ausgeprägt',
	'beginnend',
	'bereichsweise',
];
export const EXISTING_DAMAGE_OPTIONS = [
	'Nicht instandgesetzt',
	'Instandsetzung schadhaft',
	'Schadenveränderung',
	'Unverändert',
	'Schadenvergrößerung',
];

export const LONGITUDINAL_POSITION_OPTIONS = [
	{
		group: 'Objektgruppe 1',
		options: [
			'Außenseitig',
			'Binnenseitig',
			'Block',
			'Blockfuge',
			'In der Kammer',
			'Kammerseitig',
			'm X-Koordinate',
			'm vom Oberhaupt',
			'm vom Unterhaupt',
			'Mittig',
			'Oberwasserseitig',
			'Stations-km',
			'Oberhalb',
			'Unterhalb',
			'Unterwasserseitig',
		],
	},
	{
		group: 'Objektgruppe 2',
		options: [
			'm X-Koordinate',
			'm von der Wehrachse OW',
			'm von der Wehrachse UW',
			'Oberwasserseitig',
			'Unterwasserseitig',
			'Oberhalb',
			'Unterhalb',
		],
	},
	{
		group: 'Objektgruppe 3',
		options: ['Block', 'Fuge', 'Rohrschuss', 'Stoß', 'm X-Koordinate', 'Oberhalb', 'Unterhalb'],
	},
	{
		group: 'Objektgruppe 4',
		options: ['m X-Koordinate', 'Außenseitig', 'Binnenseitig', 'Mittig', 'Oberhalb', 'Unterhalb'],
	},
];

export const TRANSVERSE_POSITION_OPTIONS = [
	{
		group: 'Objektgruppe 1',
		options: [
			'Außen',
			'Innen',
			'Links',
			'm Y-Koordinate',
			'm von der Wand',
			'Mittig',
			'Rechts',
			'm von der linken Seite',
			'm von der rechten Seite',
			'Rechts und Links',
		],
	},
	{
		group: 'Objektgruppe 2',
		options: ['Links', 'm Y-Koordinate', 'm vom Pfeiler', 'm von der Wange'],
	},
	{
		group: 'Objektgruppe 3',
		options: [
			'Im Kämpferbereich',
			'Rechts und Links',
			'm von der linken Seite',
			'm von der rechten Seite',
			'Mittig',
			'Schott',
		],
	},
	{
		group: 'Objektgruppe 4',
		options: [
			'm Y-Koordinate',
			'Links',
			'Mittig',
			'Rechts',
			'Außen',
			'Innen',
			'Rechts und Links',
			'm von der linken Seite',
			'm von der rechten Seite',
		],
	},
];

export const VERTICAL_POSITION_OPTIONS = [
	{
		group: 'Objektgruppe 1',
		options: [
			'Im OW-Bereich',
			'Im UW-Bereich',
			'In der Wasserwechselzone',
			'm Z-Koordinate',
			'm über der Sohle',
			'm über NN',
			'm unter der Plattform',
			'Oberhalb',
			'Unterhalb',
			'Oben',
			'Unten',
			'Unterseitig',
		],
	},
	{
		group: 'Objektgruppe 2',
		options: [
			'm Z-Koordinate',
			'm über der Sohle',
			'm über NN',
			'm unter der Überlaufkrone',
			'Oben',
			'Unten',
			'Unterseitig',
		],
	},
	{
		group: 'Objektgruppe 3',
		options: [
			'Im Deckenbereich',
			'Im Scheitelbereich',
			'Im Sohlenbereich',
			'Links',
			'Rechts',
			'Uhr',
			'Unterseitig',
		],
	},
	{
		group: 'Objektgruppe 4',
		options: [
			'm Z-Koordinate',
			'm über der Sohle',
			'm über NN',
			'Oberhalb',
			'Unterhalb',
			'Unterseitig',
		],
	},
];

export const UNIT_TYPES = { 1: 'cm', 2: 'm²', 3: 'm³' };
